import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { LoginResponse } from '../models/security.models';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl; // Use environment variable

  // BehaviorSubjects to hold the current state and emit changes
  private loggedIn = new BehaviorSubject<boolean>(this.hasToken());
  private userRole = new BehaviorSubject<string | null>(this.getRole());
  private currentSessionId = new BehaviorSubject<string | null>(this.getSessionId());

  // Public observables for components/guards to subscribe to
  isLoggedIn$ = this.loggedIn.asObservable();
  userRole$ = this.userRole.asObservable();
  sessionId$ = this.currentSessionId.asObservable();
  isAdmin$: Observable<boolean> = this.userRole$.pipe(map(role => !!role && role.toLowerCase() === 'admin')); // Derived admin status

 // New observable for ShopeeDocsTeam role
  isShopeeDocsTeam$: Observable<boolean> = this.userRole$.pipe(
    map(role => !!role && (role.toLowerCase() === 'shopee_docs' || role.toLowerCase() === 'admin')) // Include admin users
  );

  // New observable for Shopee Checker role (mobile scanner only)
  // NOTE: Admin is NOT included here because Admin should have full access to all features,
  // and isShopeeChecker$ is used with negation (*ngIf="!isShopeeChecker$") to hide features FROM checkers
  isShopeeChecker$: Observable<boolean> = this.userRole$.pipe(
    map(role => !!role && role.toLowerCase() === 'shopeechecker')
  );

  // New observable for Warehouse role
  isWarehouse$: Observable<boolean> = this.userRole$.pipe(
    map(role => !!role && (role.toLowerCase() === 'warehouse' || role.toLowerCase() === 'admin'))
  );

  // New observable for Forwarding Admin role
  isForwardingAdmin$: Observable<boolean> = this.userRole$.pipe(
    map(role => !!role && (role.toLowerCase() === 'forwardingadmin' || role.toLowerCase() === 'admin'))
  );

  // New observable for Forwarding User role (ForwardingUser or ForwardingAdmin)
  isForwardingUser$: Observable<boolean> = this.userRole$.pipe(
    map(role => !!role && (role.toLowerCase() === 'forwardinguser' || role.toLowerCase() === 'forwardingadmin' || role.toLowerCase() === 'admin'))
  );

  constructor(private http: HttpClient, private router: Router) {}

  // Check if a token exists in localStorage and is not expired
  private hasToken(): boolean {
    const token = localStorage.getItem('loginToken');
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    
    if (!token || !expiresAt) {
      return false;
    }
    
    // Check if token is expired using the backend-provided expiration time
    const expirationTime = new Date(expiresAt).getTime();
    const now = Date.now();
    
    if (now >= expirationTime) {
      console.log('Token expired, clearing auth data');
      this.clearAuthData();
      return false;
    }
    
    return !!token;
  }

  // Get role from localStorage
  private getRole(): string | null {
    return localStorage.getItem('userRole');
  }

  // Get session ID from localStorage
  private getSessionId(): string | null {
    return localStorage.getItem('sessionId');
  }

  // Get current user information
  getCurrentUser(): { id: string; username: string; role: string } | null {
    const username = localStorage.getItem('username');
    const role = this.getRole();
    const userId = localStorage.getItem('userId');
    
    if (!username || !role) {
      return null;
    }
    
    return {
      id: userId || 'unknown',
      username: username,
      role: role
    };
  }

  // Get username
  getUsername(): string | null {
    return localStorage.getItem('username');
  }

  // Get token from localStorage (useful for interceptor or direct checks)
  getToken(): string | null {
    // Check if token is still valid before returning it
    if (this.hasToken()) {
      return localStorage.getItem('loginToken');
    }
    
    // If token is not valid, ensure observables are updated
    if (this.loggedIn.value === true) {
      console.log('Token expired during getToken() call, updating auth state');
      this.clearAuthData();
    }
    
    return null;
  }

  // Get current session ID (public method)
  getCurrentSessionId(): string | null {
    return localStorage.getItem('sessionId');
  }

  // Login method with enhanced session tracking
  login(credentials: any): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/user/login`, credentials).pipe(
      tap(response => {
        // Store enhanced login data on successful login
        if (response && response.token) {
          localStorage.setItem('loginToken', response.token);
          localStorage.setItem('tokenExpiresAt', response.expiresAt);
          localStorage.setItem('sessionId', response.sessionId);
          localStorage.setItem('loginTime', Date.now().toString()); // Keep for compatibility
          this.loggedIn.next(true);
          this.currentSessionId.next(response.sessionId);

          if (response.role) {
            localStorage.setItem('userRole', response.role);
            this.userRole.next(response.role);
            console.log('User role set from login:', response.role);
          } else {
            localStorage.removeItem('userRole');
            this.userRole.next(null);
            console.warn('Login successful, but user role not provided in response.');
          }

          // Decode JWT token to extract username and userId
          try {
            interface JwtPayload {
              'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'?: string;
              'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'?: string;
            }

            const decodedToken = jwtDecode<JwtPayload>(response.token);

            // Extract username from ClaimTypes.Name
            const username = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
            if (username) {
              localStorage.setItem('username', username);
              console.log('Username extracted from JWT:', username);
            }

            // Extract userId from ClaimTypes.NameIdentifier
            const userId = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
            if (userId) {
              localStorage.setItem('userId', userId);
              console.log('UserId extracted from JWT:', userId);
            }
          } catch (error) {
            console.error('Error decoding JWT token:', error);
          }

          // Fallback: Store username and userId if provided in response
          if (response.username) {
            localStorage.setItem('username', response.username);
          }
          if (response.userId) {
            localStorage.setItem('userId', response.userId);
          }
        } else {
          throw new Error('Login response missing token.');
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Clear state on HTTP error during login
        this.clearAuthData();
        console.error('AuthService Login Error:', error);
        return throwError(() => error);
      })
    );
  }

  // Enhanced logout method that notifies backend
  logout(): Observable<any> {
    const sessionId = this.getSessionId();
    
    // Clear local data immediately
    this.clearAuthData();
    
    // Navigate to login
    this.router.navigate(['/login']);
    
    // Notify backend about logout if we have a session
    if (sessionId) {
      return this.http.post(`${this.apiUrl}/user/logout`, { sessionId }).pipe(
        catchError((error) => {
          console.warn('Logout notification to backend failed:', error);
          return of(null); // Don't fail the logout process
        })
      );
    }
    
    return of(null);
  }

  // Helper to clear auth data and update subjects
  private clearAuthData(): void {
    localStorage.removeItem('loginToken');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('userId');
    this.loggedIn.next(false);
    this.userRole.next(null);
    this.currentSessionId.next(null);
  }

  // Method to check if the current user is admin (synchronous check)
  isAdmin(): boolean {
    const role = this.getRole();
    return !!role && role.toLowerCase() === 'admin';
  }

  // Synchronous check for ShopeeDocsTeam (optional, if needed)
  isShopeeDocsTeam(): boolean {
    const role = this.getRole();
    return !!role && role.toLowerCase() === 'shopee_docs'; // Fixed to match the actual role string
  }

  // Synchronous check for Warehouse role
  isWarehouse(): boolean {
    const role = this.getRole();
    return !!role && (role.toLowerCase() === 'warehouse' || role.toLowerCase() === 'admin');
  }

  // Synchronous check for Manager role
  isManager(): boolean {
    const role = this.getRole();
    return !!role && (role.toLowerCase() === 'manager' || role.toLowerCase() === 'admin');
  }

  // Synchronous check for Forwarding Admin role
  isForwardingAdmin(): boolean {
    const role = this.getRole();
    return !!role && (role.toLowerCase() === 'forwardingadmin' || role.toLowerCase() === 'admin');
  }

  // Synchronous check for Forwarding User role (includes ForwardingAdmin)
  isForwardingUser(): boolean {
    const role = this.getRole();
    return !!role && (role.toLowerCase() === 'forwardinguser' || role.toLowerCase() === 'forwardingadmin' || role.toLowerCase() === 'admin');
  }

  // Generic role check method
  hasRole(requiredRole: string): boolean {
    const role = this.getRole();
    if (!role) return false;
    
    // Admin has access to everything
    if (role.toLowerCase() === 'admin') return true;
    
    return role.toLowerCase() === requiredRole.toLowerCase();
  }

  // Check if user has any of the required roles
  hasAnyRole(requiredRoles: string[]): boolean {
    const role = this.getRole();
    if (!role) return false;
    
    // Admin has access to everything
    if (role.toLowerCase() === 'admin') return true;
    
    const normalizedUserRole = role.toLowerCase();
    return requiredRoles.some(requiredRole => 
      normalizedUserRole === requiredRole.toLowerCase()
    );
  }

  // Observable versions for reactive components
  hasRole$(requiredRole: string): Observable<boolean> {
    return this.userRole$.pipe(
      map(role => {
        if (!role) return false;
        if (role.toLowerCase() === 'admin') return true;
        return role.toLowerCase() === requiredRole.toLowerCase();
      })
    );
  }

  hasAnyRole$(requiredRoles: string[]): Observable<boolean> {
    return this.userRole$.pipe(
      map(role => {
        if (!role) return false;
        if (role.toLowerCase() === 'admin') return true;
        
        const normalizedUserRole = role.toLowerCase();
        return requiredRoles.some(requiredRole => 
          normalizedUserRole === requiredRole.toLowerCase()
        );
      })
    );
  }

  // Handle 401 responses by automatically logging out
  handle401Error(): void {
    console.log('Received 401 error, token likely expired. Logging out...');
    this.logout();
  }

  // Get remaining token time in minutes
  getTokenTimeRemaining(): number {
    const loginTime = localStorage.getItem('loginTime');
    if (!loginTime) return 0;
    
    const tokenAge = Date.now() - parseInt(loginTime);
    const thirtyMinutes = 30 * 60 * 1000;
    const remainingTime = thirtyMinutes - tokenAge;
    
    return Math.max(0, Math.floor(remainingTime / (60 * 1000))); // Return minutes
  }
}